import _, { set } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { generateClient } from '@aws-amplify/api';
import { getSites as _getSites, getStudies as _getStudies } from '@/graphql/queries';
import { uploadProtocol as _uploadProtocol, upsertStudy as _upsertStudy } from '@/graphql/mutations';
import { onUpsertStudy } from '@/graphql/subscriptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import HeaderWithTabs from '@/HeaderWithTabs'
import ErrorModal from '@/ErrorModal';
import Breadcrumb from '@/Breadcrumb';
import { dateField, selectField, textAreaField, textField } from '@/commonFields';
import useFormFields from '@/hooks/useFormFields';
import FilterCreator from '@/components/FilterCreator';
import RootContext from '@/RootContext';

const client = generateClient();

const getObject = ({ id }) => {
	return client.graphql({ query: _getStudies, variables: { id } })
		.then(response => {
			const data = response.data.getStudies.studies?.[0];
			try {
				// Normalize protocol_extract_json into object
				if (data.protocol_extract_json) data.protocol_extract_json = JSON.parse(data.protocol_extract_json);
			} catch (error) {}
			return data;
		});
};

const upsertObject = ({ input }) => {
	return client.graphql({ query: _upsertStudy, variables: { input } })
		.then(response => {
			return response.data.upsertStudy;
		});
};

const uploadProtocol = ({ content_type, filename, study_id }) => {
	return client.graphql({ query: _uploadProtocol, variables: { input: { content_type, filename, study_id } } })
		.then(response => {
			return response.data.uploadProtocol;
		});
};

const getSites = () => {
	return client.graphql({ query: _getSites })
		.then(response => {
			return response.data.getSites.sites;
		});
};

export default ({ createLabel = 'Create', links = [], onCancel, onSave, updateLabel = 'Save' }) => {
	const {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		jsonModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	} = useFormFields();

	const isConstructed = useRef(false);
	const [errorMessage, setErrorMessage] = useState();
	const [isLoaded, setIsLoaded] = useState(isCreate);
	const [isSaving, setIsSaving] = useState(false);
	const subscriptions = useRef([]);
	const rootContext = useContext(RootContext);
	const auth = rootContext.get('auth');

	const leftObj = _.cloneDeep(sanitizeObject(model));
	delete leftObj.studies?.protocol_extract_json;
	const rightObj = _.cloneDeep(origModel.current);
	delete rightObj.studies?.protocol_extract_json;
	const isEqual = _.isEqualWith(leftObj, rightObj);

	const [inExpand, setInExpand] = useState({});
	const [exExpand, setExExpand] = useState({});
	const toggleInExpand = i => setInExpand(prev => ({ ...prev, [i]: !prev[i] }));
	const toggleExExpand = i => setExExpand(prev => ({ ...prev, [i]: !prev[i] }));

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setIsLoaded(true);
		setIsSaving(false);
	};

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (!isLoaded) {
			getObject({ id })
				.then(data => {
					origModel.current = { studies: data };
					setModel({ studies: data });
					setIsLoaded(true);
				})
				.catch(catchError);
		}
	}

	useEffect(() => {
		if (!isCreate) {
			// We only need this for getting protocol upload updates
			subscriptions.current.push(client.graphql({	query: onUpsertStudy, variables: {} }).subscribe({
			next: ({ data }) => {
				const study = data.onUpsertStudy.studies[0];
				// No efficient way to filter subscription (would need top-level arg) for study_id so we need to ignore others
				if (study.id !== id) return;
				try {
					// Normalize protocol_extract_json into object
					if (study.protocol_extract_json) study.protocol_extract_json = JSON.parse(study.protocol_extract_json);
					set_value({ key: 'studies', name: 'protocol_extract_json', value: study.protocol_extract_json });
				} catch (error) {}
			},
			error: (error) => {
				console.error("Subscription error:", error);
			}
			}));
			return () => {
				subscriptions.current.forEach(e => e.unsubscribe());
			};
		}
	}, []);

	const setFile = useCallback(async event => {
		try {
			event.persist();
			const file = event.target.files?.[0];
			console.log('file', file);
			const response = await uploadProtocol({ content_type: file.type, filename: file.name, study_id: id });
			console.log('response', response);
			set_value({ key: 'studies', name: 'protocol_extract_json', value: { request_id: response.request_id, status: 'Processing...' } });
			// Upload the file
			const upload_response = await fetch(response.upload_url, {
				method: 'PUT',
				headers: {
					'Content-Type': file.type
				},
				body: file
			});
			if (upload_response.ok) {
				console.log('Upload successful!', upload_response);
			} else {
				console.error('Upload failed');
			}
			/*
			// TESTING
			setTimeout(() => {
				set_value({
					key: 'studies',
					name: 'protocol_extract_json',
					value: test_protocol_extract
				});
			}, 5000);
			*/
		} catch (error) {
			event.target.value = '';
			catchError(error);
		}
	}, [JSON.stringify(model)]);

	const details = useMemo(() => (
		<div className="row mt-5">
			<div className="col-6">
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{textField({ autoFocus: isCreate, get: get_value, getError, set: set_value, key: 'studies', label: 'Study Name', name: 'study_name' })}
						</div>
						<div className="col-4">
							{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Indication', name: 'study_category' })}
						</div>
					</div>
					{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Study Description', name: 'study_description' })}
					{selectField({ defaultOptions: model.studies ? [{ id: model.studies.site_id, site_description: model.studies.site_description, site_name: model.studies.site_name }] : [], get: get_value, getData: getSites, getError, set: set_value, key: 'studies', label: 'Site', label1Key: 'site_name', label2Key: 'site_description', name: 'site_id', idKey: 'id', valueKey: 'id' })}
					<div className="row">
						<div className="col-3">
							{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Sponsor ID', name: 'sponsor_id' })}
						</div>
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Sponsor Name', name: 'sponsor_name' })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{dateField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Start Date', name: 'start_date' })}
						</div>
						<div className="col">
							{dateField({ get: get_value, getError, set: set_value, key: 'studies', label: 'End Date', name: 'end_date' })}
						</div>
					</div>
					{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Status', name: 'status' })}
					{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Visit Name', name: 'visit_name' })}
				</div>
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const protocol = useMemo(() => (
		<div className="row mt-5 align-items-center">
			<div className="col-auto">
				{model.studies?.protocol_extract_json?.status ? (
					<button className="btn btn-primary position-relative" style={{top:'40%'}} type="button" disabled>
						<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
						{model.studies.protocol_extract_json.status}
					</button>
				) : (
					<div className="border shadow p-3 bg-form">
						<div className="row">
							<>
								<input
									accept=".pdf,.docx,.txt"
									className="d-none"
									disabled={isCreate}
									id="uploadProtocol"
									onChange={setFile}
									title={isCreate ? 'Must save study before uploading protocol' : ''}
									type="file"
								/>
								<label htmlFor="uploadProtocol">
									<span className="btn btn-light">Upload protocol</span>
								</label>
							</>
						</div>
					</div>
				)}
			</div>
			{model.studies?.protocol_extract_json?.inclusionary || model.studies?.protocol_extract_json?.exclusionary ? (
				<div className="row mt-4 mb-3">
					<div className="col">
						<div className="mb-3"><b><u>Inclusion Criteria</u></b></div>
						<div className="border shadow p-3 bg-form">
							<div className="row" style={{maxHeight: '30vh', overflowY: 'auto'}}>
								<div className="col">
									{(model.studies.protocol_extract_json.inclusionary || []).map((e, i) => {
										return (
											<div className="row mb-2 cursor-pointer" key={i} onClick={() => toggleInExpand(i)}>
												<div className="col-auto pe-0 width_55">
													<FontAwesomeIcon className="me-2" icon={inExpand[i] ? icons_solid.faChevronDown : icons_solid.faChevronRight} style={{fontSize: '10px', width: '10px'}} />
													<small><b>{i + 1}.</b></small>
												</div>
												<div className="col ps-0">
													<span>{e.question}</span>
												</div>
												{inExpand[i] && (
													<>
														<div className="row mt-1">
															<div className="col-auto pe-0 me-2 width_50 border-end border-dark"></div>
															<div className="col ps-0">
																<span>{e.protocol_criteria}</span>
															</div>
														</div>
														<div className="row mt-1">
															<div className="col-auto pe-0 me-2 width_50"></div>
															<div className="col ps-0">
																<span><i>{e.protocol_source}</i></span>
															</div>
														</div>
													</>
												)}
											</div>
										);
									})}
								</div>
							</div>
						</div>
						<div className="row mt-4 mb-3">
							<div className="mb-3"><b><u>Inclusion Filters</u></b></div>
							<FilterCreator get={() => get_value({ key: 'studies', name: 'subject_inclusion_filters_json' })} set={value => set_value({ key: 'studies', name: 'subject_inclusion_filters_json', value })} />
						</div>
					</div>
					<div className="col">
						<div className="mb-3"><b><u>Exclusion Criteria</u></b></div>
						<div className="border shadow p-3 bg-form">
							<div className="row" style={{maxHeight: '30vh', overflowY: 'auto'}}>
								<div className="col">
									{(model.studies.protocol_extract_json.exclusionary || []).map((e, i) => {
										return (
											<div className="row mb-2 cursor-pointer" key={i} onClick={() => toggleExExpand(i)}>
												<div className="col-auto pe-0 width_55">
													<FontAwesomeIcon className="me-2" icon={exExpand[i] ? icons_solid.faChevronDown : icons_solid.faChevronRight} style={{fontSize: '10px', width: '10px'}} />
													<small><b>{i + 1}.</b></small>
												</div>
												<div className="col ps-0">
													<span>{e.question}</span>
												</div>
												{exExpand[i] && (
													<>
														<div className="row mt-1">
															<div className="col-auto pe-0 me-2 width_50 border-end border-dark"></div>
															<div className="col ps-0">
																<span>{e.protocol_criteria}</span>
															</div>
														</div>
														<div className="row mt-1">
															<div className="col-auto pe-0 me-2 width_50"></div>
															<div className="col ps-0">
																<span><i>{e.protocol_source}</i></span>
															</div>
														</div>
													</>
												)}
											</div>
										);
									})}
								</div>
							</div>
						</div>
						<div className="row mt-4 mb-3">
							<div className="mb-3"><b><u>Exclusion Filters</u></b></div>
							<FilterCreator get={() => get_value({ key: 'studies', name: 'subject_exclusion_filters_json' })} set={value => set_value({ key: 'studies', name: 'subject_exclusion_filters_json', value })} />
						</div>
					</div>
				</div>
			) : undefined}
		</div>
	), [JSON.stringify(errors), JSON.stringify(model), JSON.stringify(inExpand), JSON.stringify(exExpand)]);

	const administration = useMemo(() => (
		<div className="row mt-5">
			<div className="col-6">
				<div className="mb-3"><b><u>Agent Configuration</u></b></div>
				<div className="border shadow p-3 bg-form">
					<div className="row">
						<div className="col">
							{textAreaField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Pre-call text', name: 'agent_precall_text', rows: 5 })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{textAreaField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Post-call booked_appointment text', name: 'agent_booked_appointment_text', rows: 5  })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{textAreaField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Post-call needs_callback text', name: 'agent_needs_callback_text', rows: 5  })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Text Agent Name', name: 'text_agent_name' })}
						</div>
					</div>
					<div className="row">
						<div className="col">
							{textField({ get: get_value, getError, set: set_value, key: 'studies', label: 'Voice Agent Name', name: 'voice_agent_name' })}
						</div>
					</div>
				</div>
			</div>
			{!isCreate && (
				<div className="col-6">
					<div className="mb-3"><b><u>Clinical Conductor</u></b></div>
					<div className="border shadow p-3 bg-form">
						<div className="row">
							<div className="col-auto width_100">
								{textField({ disabled: true, get: get_value, getError, set: set_value, key: 'studies', label: 'CCID', name: 'ccid' })}
							</div>
							<div className="col">
								<div className="mb-3 small"><b>Study Visits</b></div>
								{(model.studies?.study_cc_visits || []).length > 0 ? (
									<>
										<div className="row">
											<div className="col-auto width_60 small"><b>CCID</b></div>
											<div className="col small"><b>NAME</b></div>
										</div>
										<hr />
										{(model.studies?.study_cc_visits || []).map((e, i) => (
											<div className="row" key={i}>
												<div className="col-auto width_60">{e.id}</div>
												<div className="col">{e.name}</div>
											</div>
										))}
									</>
								) : (
									<div className="row">
										<div className="col-auto mx-auto"><b><i>No Results</i></b></div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const tabs = [
		{ name: 'details', label: 'Details', content: details },
		...(!isCreate ? [{ name: 'protocol', label: 'Protocol', content: protocol }] : []),
		...(auth?.groups?.includes('admin') ? [{ name: 'administration', label: 'Administration', content: administration }] : [])
	];

	const handleSave = () => {
		// Extract input from model
		const input = {
			...changeModel.current,
			studies: {
				...changeModel.current.studies,
				...(!isCreate ? { id } : undefined)
			}
		};
		console.log('model', model);
		const newErrors = {
			studies: {}
		};
		if (!hasValue({ key: 'studies', name: 'study_name' })) {
			newErrors.studies.study_name = 'Study Name is required';
		}
		if (!hasValue({ key: 'studies', name: 'site_id' })) {
			newErrors.studies.site_id = 'Site is required';
		}
		// Clean out empties
		Object.keys(newErrors).forEach(e => {
			if (
				(Array.isArray(newErrors[e]) && newErrors[e].length === 0) ||
				Object.keys(newErrors[e]).length === 0
			) {
				delete newErrors[e];
			}
		});
		// Never storing this from this form
		delete input.studies.protocol_extract_json;
		// JSON to strings
		input.studies.subject_inclusion_filters_json = JSON.stringify(input.studies.subject_inclusion_filters_json);
		input.studies.subject_exclusion_filters_json = JSON.stringify(input.studies.subject_exclusion_filters_json);
		console.log('newErrors', newErrors);
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}
		console.log('input', input);
		console.log('deleteModel', deleteModel.current);
		setIsSaving(true);
		upsertObject({ input, ...(deleteModel.current.length > 0 ? { input_delete: deleteModel.current } : undefined) })
			.then(response => {
				console.log('response', response);
				setIsSaving(false);
				onSave && onSave();
			})
			.catch(catchError);
	};

	const handleCancel = () => onCancel && onCancel();

	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<div className="container-fluid px-4 position-relative">
				<HeaderWithTabs
					isLoaded={isLoaded}
					path="/app/customers/"
					tabs={tabs}
				>
					<>
						{!isLoaded ? (
							<div className="text-center" style={{marginTop:'5rem'}}>
								<button className="btn btn-primary" type="button" disabled>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Loading...
								</button>
							</div>
						) : (
							<>
								{links.length > 0 && <Breadcrumb className="mb-3 px-3" links={links} />}
								<div className="row align-items-center position-absolute py-1 me-5" style={{right:0}}>
									<button className="col-auto ml-auto my-0 btn btn-primary" disabled={!((isCreate || !isEqual) && !isSaving)} onClick={handleSave}>{isCreate ? createLabel : updateLabel }</button>
									<a className="col-auto" role="button" onClick={handleCancel}>Cancel</a>
								</div>
								{isSaving && (
									<div className="text-center position-absolute" style={{height:'100%',left:0,top:0, width:'100%',zIndex:1}}>
										<button className="btn btn-primary position-relative" style={{top:'40%'}} type="button" disabled>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											Saving...
										</button>
									</div>
								)}
							</>
						)}
					</>
				</HeaderWithTabs>
			</div>
		</>
	);
};
